
import Link from "@/components/ui/link-compat";
import { useState, useCallback } from "react";
import { Building2, Trash2, User } from "lucide-react";
import { ContactType, type Contact } from "@/types/crm";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Breadcrumb } from "@/components/ui/breadcrumb";
import { AskAiButton } from "@/components/assistant/assistant-provider";

interface ContactDetailHeaderProps {
  contact: Contact;
  onSaveName: (name: string) => void;
  onDelete?: () => void;
}

export function ContactDetailHeader({ contact, onSaveName, onDelete }: ContactDetailHeaderProps) {
  const isCompany = contact.type === ContactType.Company;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");

  const startEdit = useCallback(() => {
    setDraft(contact.name);
    setEditing(true);
  }, [contact.name]);

  const commit = useCallback(() => {
    setEditing(false);
    const trimmed = draft.trim();
    if (trimmed && trimmed !== contact.name) {
      onSaveName(trimmed);
    }
  }, [draft, contact.name, onSaveName]);

  const cancel = useCallback(() => {
    setEditing(false);
  }, []);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") { e.preventDefault(); commit(); }
      else if (e.key === "Escape") { e.preventDefault(); cancel(); }
    },
    [commit, cancel]
  );

  return (
    <div className="space-y-1.5">
      <Breadcrumb
        items={[
          { label: "Contacts", href: "/dashboard/contacts" },
          { label: contact.name },
        ]}
      />
      <div className="flex items-center gap-2 flex-wrap">
        {isCompany ? (
          <Building2 className="h-5 w-5 text-muted-foreground shrink-0" />
        ) : (
          <User className="h-5 w-5 text-muted-foreground shrink-0" />
        )}
        {editing ? (
          <input
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKeyDown}
            onBlur={commit}
            className="text-2xl font-semibold tracking-tight bg-transparent border-b-2 border-primary outline-none w-full max-w-md"
          />
        ) : (
          <h1
            className="text-2xl font-semibold tracking-tight truncate cursor-text hover:opacity-80 transition-opacity"
            onClick={startEdit}
            title="Click to edit"
          >
            {contact.name}
          </h1>
        )}
        <Badge variant="outline" className="text-xs">
          {isCompany ? "Company" : "Person"}
        </Badge>
        <div className="ml-auto flex items-center gap-1">
          <AskAiButton />
          {onDelete && (
            <Button variant="ghost" size="icon" className="size-9 text-muted-foreground hover:text-destructive" onClick={onDelete} title="Delete contact">
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
      {!isCompany && contact.companyId && (
        <Link
          href={`/dashboard/contact?id=${contact.companyId}`}
          className="text-sm text-muted-foreground hover:text-foreground hover:underline inline-flex items-center gap-1 transition-colors"
        >
          <Building2 className="h-3.5 w-3.5" />
          {contact.companyName}
        </Link>
      )}
    </div>
  );
}
